import React, { useState, useEffect } from 'react';
import { Turnstile } from "@marsidev/react-turnstile";

// Design imports
import './LightningSubscription.css';
import { AlertLocationMapPicker } from './AlertLocationMapPicker'
import './AlertLocationMapPicker.css';
import { NotificationScheduleSelector, NotificationSchedule } from './NotificationScheduleSelector';
import './NotificationScheduleSelector.css';

// Map imports
import { useMap } from 'react-leaflet';

// Assets
import fnnLogo from "../../client-dashboard/client-dashboard-assets/FNNLogoWithBlackFont-06.png"

// Icons
import {
  LuChevronDown,
  LuChevronRight,
} from 'react-icons/lu';

// ─── Session Auth ─────────────────────────────────────────────────────────────
// Module-level variable: lives for the lifetime of the JS bundle (i.e. until
// the user refreshes or closes the tab). Never written to localStorage.

interface SessionAuth {
  email: string;
  verifiedToken: string;
}

let _sessionAuth: SessionAuth | null = null;

const getSessionAuth = (): SessionAuth | null => _sessionAuth;

const setSessionAuth = (auth: SessionAuth | null): void => {
  _sessionAuth = auth;
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface Organization {
  org_id: number;
  org_name: string;
}

interface Location {
  location_id: number;
  location_name: string;
  lat: string;
  lon: string;
}

// Storm Map only — includes live storm status, fetched from the
// subscriber-gated endpoint rather than the public picker list.
interface LocationWithStorm extends Location {
  last_seen_strike_time: string | null;
  is_active_storm: boolean;
}

interface TermsAndConditions {
  version: number;
  body: string;
  created_at: string;
}

interface SubscriberState {
  name: string;
  phone_number: string | null;
  organization: number | null;
  email_alerts_enabled: boolean;
  sms_alerts_enabled: boolean;
  sms_status: 'not_requested' | 'pending' | 'approved' | 'denied';
  location_ids: number[];
  notification_schedule: NotificationSchedule;
}

type DeliveryMethod = 'email' | 'email_sms' | 'sms';
type View = 'menu' | 'subscribe' | 'manage' | 'unsubscribe' | 'storm_map';
type Status = 'idle' | 'loading' | 'success' | 'error';
type OtpStatus = 'idle' | 'sending' | 'sent' | 'verifying' | 'verified' | 'error';

// ─── Constants ────────────────────────────────────────────────────────────────

const TURNSTILE_SITE_KEY = "0x4AAAAAACiHB4c31UPAbHHe";
const API_BASE = "/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fetchCsrfToken = async (): Promise<string | null> => {
  const response = await fetch(`${API_BASE}/csrf-token/`, {
    method: 'GET',
    credentials: "same-origin",
  });
  if (response.ok) {
    const data = await response.json();
    return data.csrfToken;
  }
  console.error('Failed to fetch CSRF token');
  return null;
};

const normalizePhoneNumber = (input: string): string => {
  if (!input) return "";
  const value = input.trim();
  if (value.startsWith("+")) return "+" + value.slice(1).replace(/\D/g, "");
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) return "+1" + digits;
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  return "+" + digits;
};

// ─── Shared Sub-components ────────────────────────────────────────────────────

const StormIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 20C14 14.477 18.477 10 24 10C28.663 10 32.596 13.096 33.699 17.35C36.668 17.956 39 20.618 39 23.833C39 27.5 36.038 30.5 32.333 30.5H15.667C11.962 30.5 9 27.5 9 23.833C9 21.192 10.612 18.92 13 17.958"
      stroke="#28AAE1" strokeWidth="2" strokeLinecap="round"
    />
    <path
      d="M26 30.5L22 37.5H27L23 44.5"
      stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

const BackArrow = ({ onClick }: { onClick: () => void }) => (
  <button className="ls-back-btn" onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    Back
  </button>
);

const LocationCheckbox = ({
  loc, checked, onChange,
}: { loc: Location; checked: boolean; onChange: (id: number) => void }) => (
  <label className={`ls-checkbox-item ${checked ? 'ls-checked' : ''}`}>
    <div className={`ls-checkbox-box ${checked ? 'ls-checked' : ''}`}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <input type="checkbox" className="ls-checkbox-hidden" checked={checked} onChange={() => onChange(loc.location_id)} />
    <span className={`ls-checkbox-text ${checked ? 'ls-checked' : ''}`}>{loc.location_name}</span>
  </label>
);

const StatusBanner = ({ status, message }: { status: 'success' | 'error'; message: string }) => (
  <div className={`ls-banner ${status}`}>{message}</div>
);

const OrganizationSelector = ({
  organizations,
  value,
  onChange,
}: {
  organizations: Organization[];
  value: number | null;
  onChange: (v: number | null) => void;
}) => (
  <div className="ls-field">
    <label className="ls-label">Who are you with?</label>

    <div className="ls-select-wrapper">
      <select
        className="ls-input ls-select-input"
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            e.target.value ? Number(e.target.value) : null
          )
        }
      >
        <option value="">
          Independent / Not affiliated
        </option>

        {organizations.map(org => (
          <option key={org.org_id} value={org.org_id}>
            {org.org_name}
          </option>
        ))}
      </select>

      <div className="ls-select-chevron">
        <LuChevronDown size={18} />
      </div>
    </div>
  </div>
);

// ─── OTP Screen (reusable) ────────────────────────────────────────────────────

interface OtpScreenProps {
  email: string;
  otpCode: string;
  otpStatus: OtpStatus;
  otpMessage: string;
  confirmLabel: string;
  confirmLabelLoading: string;
  isDanger?: boolean;
  onCodeChange: (val: string) => void;
  onConfirm: () => void;
  onResend: () => void;
  onBack: () => void;
  subtitle?: string;
}

const OtpScreen = ({
  email, otpCode, otpStatus, otpMessage,
  confirmLabel, confirmLabelLoading, isDanger,
  onCodeChange, onConfirm, onResend, onBack, subtitle,
}: OtpScreenProps) => (
  <div>
    <BackArrow onClick={onBack} />
    <h2 className="ls-view-title">Verify your email</h2>
    <p className="ls-view-subtitle">
      {subtitle || <>We sent a 6-digit code to <strong>{email}</strong>. Enter it below to continue.</>}
    </p>
    <div className="ls-otp-standalone">
      <div className="ls-otp-verify-row">
        <input
          className="ls-input ls-otp-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={otpCode}
          onChange={e => onCodeChange(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      {otpMessage && (
        <p className={`ls-otp-msg ${otpStatus === 'error' ? 'ls-otp-msg-error' : 'ls-otp-msg-info'}`}>
          {otpMessage}
        </p>
      )}
      <button
        type="button"
        className={`ls-submit-btn${isDanger ? ' danger' : ''}`}
        style={{ marginTop: '16px' }}
        disabled={otpStatus === 'verifying'}
        onClick={onConfirm}
      >
        {otpStatus === 'verifying' ? confirmLabelLoading : confirmLabel}
      </button>
      <button type="button" className="ls-resend-btn" onClick={onResend}>
        Didn&apos;t receive a code? Resend
      </button>
    </div>
  </div>
);

// ─── Terms and Conditions ─────────────────────────────────────────────────────

const TermsAndConditionsField = ({
  agreed, onChange, terms,
}: {
  agreed: boolean;
  onChange: (val: boolean) => void;
  terms: TermsAndConditions | null;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="ls-field-last ls-terms-wrapper">
      <div className="ls-terms-box">
        <button type="button" className="ls-terms-toggle" onClick={() => setExpanded(prev => !prev)}>
          <span className="ls-terms-title">Terms and Conditions</span>
          <svg className={`ls-terms-chevron ${expanded ? 'ls-terms-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="#28AAE1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {expanded && (
          <div className="ls-terms-body">
            {terms
              ? <p style={{ whiteSpace: 'pre-wrap' }}>{terms.body}</p>
              : <p>Loading Terms and Conditions...</p>
            }
          </div>
        )}
      </div>
      <label className={`ls-checkbox-item ls-terms-checkbox-row ${agreed ? 'ls-checked' : ''}`}>
        <div className={`ls-checkbox-box ${agreed ? 'ls-checked' : ''}`}>
          {agreed && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <input type="checkbox" className="ls-checkbox-hidden" checked={agreed} onChange={e => onChange(e.target.checked)} />
        <span className={`ls-checkbox-text ${agreed ? 'ls-checked' : ''}`}>
          I have read and agree to the <span className="ls-terms-highlight">Terms and Conditions</span>
        </span>
      </label>
    </div>
  );
};

// ─── Delivery Method Selector ─────────────────────────────────────────────────

const DeliveryMethodSelector = ({
  value, onChange,
}: { value: DeliveryMethod; onChange: (v: DeliveryMethod) => void }) => {
  const options: { value: DeliveryMethod; label: string; desc: string; badge?: string }[] = [
    {
      value: 'email',
      label: 'Email only',
      desc: 'Receive alerts via email. Starts immediately after subscribing.',
    },
    {
      value: 'email_sms',
      label: 'Email + SMS',
      desc: 'Receive alerts via both email and SMS.',
      badge: 'Requires approval',
    },
    {
      value: 'sms',
      label: 'SMS only',
      desc: 'Receive alerts via SMS only.',
      badge: 'Requires approval',
    },
  ];

  return (
    <div className="ls-field-last">
      <label className="ls-label">How would you like to receive alerts? *</label>
      <div className="ls-delivery-options">
        {options.map(opt => (
          <label
            key={opt.value}
            className={`ls-delivery-option ${value === opt.value ? 'ls-delivery-selected' : ''}`}
          >
            <input
              type="radio"
              name="deliveryMethod"
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="ls-checkbox-hidden"
            />
            <div className={`ls-delivery-radio ${value === opt.value ? 'ls-checked' : ''}`} />
            <div className="ls-delivery-text">
              <span className="ls-delivery-label">
                {opt.label}
                {opt.badge && <span className="ls-delivery-badge">{opt.badge}</span>}
              </span>
              <span className="ls-delivery-desc">{opt.desc}</span>
            </div>
          </label>
        ))}
      </div>
      {(value === 'email_sms' || value === 'sms') && (
        <div className="ls-info-box">
          📋 SMS alerts require manual review. You&apos;ll receive an email once your request has been approved or denied. Email alerts (if selected) begin immediately.
        </div>
      )}
    </div>
  );
};

// ─── Menu View ────────────────────────────────────────────────────────────────

const MenuView = ({
  onSelect,
  sessionAuth,
  onClearSession,
}: {
  onSelect: (v: View) => void;
  sessionAuth: SessionAuth | null;
  onClearSession: () => void;
}) => {
  const options: { view: View; icon: string; label: string; desc: string; disabled?: boolean }[] = [
    {
      view: 'storm_map',
      icon: '🗺️',
      label: 'Storm Map',
      desc: 'See real-time lightning activity near monitored locations.',
    },
    {
      view: 'subscribe',
      icon: '📬',
      label: 'Subscribe to Alerts',
      desc: sessionAuth
        ? `Already subscribed as ${sessionAuth.email}.`
        : 'Sign up for lightning alerts via email, SMS, or both.',
      disabled: !!sessionAuth,
    },
    {
      view: 'manage',
      icon: '⚙️',
      label: 'Manage Subscription',
      desc: 'Manage your alert locations and how the alerts are delivered to you.',
    },
    {
      view: 'unsubscribe',
      icon: '🚫',
      label: 'Unsubscribe',
      desc: 'Remove yourself from all lightning alerts.',
    },
  ];

  return (
    <div>
      <div className="ls-menu-header">
        <h1 className="ls-menu-title"><StormIcon /> Lightning Alerts</h1>
        <p className="ls-menu-subtitle">Real-time lightning notifications for your locations</p>
      </div>

      {sessionAuth && (
        <div className="ls-session-banner">
          <span>
            <span className="ls-session-dot" />
            Signed in as <strong style={{ marginLeft: '0.25rem' }}>{sessionAuth.email}</strong>
          </span>
          <button className="ls-session-signout" onClick={onClearSession}>
            Sign out
          </button>
        </div>
      )}

      <div className="ls-menu-options">
        {options.map(({ view, icon, label, desc, disabled }) => (
          <button
            key={view}
            className={`ls-menu-option-btn${disabled ? ' ls-menu-option-disabled' : ''}`}
            onClick={() => !disabled && onSelect(view)}
            disabled={disabled}
          >
            <span className="ls-menu-option-icon">{icon}</span>
            <div>
              <div className="ls-menu-option-label">{label}</div>
              <div className="ls-menu-option-desc">{desc}</div>
            </div>
            {!disabled && (
              <svg className="ls-menu-option-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Subscribe View ───────────────────────────────────────────────────────────

const SubscribeView = ({
  onBack, locations, terms, organizations, sessionAuth, onSessionAuth,
}: {
  onBack: () => void;
  locations: Location[];
  terms: TermsAndConditions | null;
  organizations: Organization[];
  sessionAuth: SessionAuth | null;
  onSessionAuth: (auth: SessionAuth) => void;
}) => {
  const [form, setForm] = useState({ name: '', email: sessionAuth?.email ?? '', phone_number: '' });
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('email');
  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(sessionAuth?.verifiedToken ?? null);

  const [showTurnstile, setShowTurnstile] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [organization, setOrganization] = useState<number | null>(null);

  const needsPhone = deliveryMethod === 'email_sms' || deliveryMethod === 'sms';
  const [notificationSchedule, setNotificationSchedule] = useState<NotificationSchedule>({});

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // STEP 1 — validate & either skip OTP (session exists) or show Turnstile
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setStatus('error'); setMessage('Please enter your name.'); return; }
    if (!form.email.trim()) { setStatus('error'); setMessage('Please enter your email.'); return; }
    if (needsPhone && !form.phone_number.trim()) {
      setStatus('error'); setMessage('Please enter your phone number for SMS alerts.'); return;
    }
    if (selectedLocs.length === 0) { setStatus('error'); setMessage('Please select at least one location.'); return; }
    if (!agreedToTerms) { setStatus('error'); setMessage('You must agree to the Terms and Conditions to subscribe.'); return; }
    setStatus('idle'); setMessage('');

    // If we already have a session token for this email, skip Turnstile + OTP
    if (sessionAuth && sessionAuth.email === form.email.trim().toLowerCase() && sessionAuth.verifiedToken) {
      setVerifiedToken(sessionAuth.verifiedToken);
      handleSubscribeWithToken(sessionAuth.verifiedToken);
    } else {
      setShowTurnstile(true);
    }
  };

  // Direct subscribe using an already-verified token (session bypass)
  const handleSubscribeWithToken = async (token: string) => {
    setStatus('loading');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone_number: needsPhone ? normalizePhoneNumber(form.phone_number) : undefined,
        locations: selectedLocs,
        terms_version: terms?.version,
        verified_token: token,
        delivery_method: deliveryMethod,
        organization,
        notification_schedule: notificationSchedule,
      };

      const endpoint = deliveryMethod === 'email'
        ? `${API_BASE}/public/sms_subscribe/`
        : `${API_BASE}/public/sms_sub_request/`;

      const submitRes = await fetch(endpoint, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify(payload),
      });
      const submitData = await submitRes.json();
      if (!submitRes.ok) throw new Error(submitData.error || 'Subscription failed.');

      setStatus('success');
      setMessage(
        deliveryMethod === 'email'
          ? "You're subscribed! You'll receive email alerts when storms are detected near your locations."
          : submitData.message || "Your request has been submitted. You'll receive an email once it's been reviewed."
      );
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Subscription failed.');
    }
  };

  // STEP 2 — Turnstile → send OTP
  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');
    setOtpMessage('');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, turnstileToken: token, flow: 'subscribe' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${form.email}. It expires in 10 minutes.`);
    } catch (err: any) {
      const errorMessage = err.message || 'Could not send verification code.';
      if (errorMessage.includes('already subscribed')) {
        setOtpStatus('idle'); setStatus('error'); setMessage(errorMessage); return;
      }
      setOtpStatus('error'); setOtpMessage(errorMessage);
    }
  };

  // STEP 3 — verify OTP → subscribe
  const handleVerifyAndSubscribe = async () => {
    if (otpCode.length !== 6) { setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return; }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);
      setOtpStatus('verified');

      // Persist to session so other views can skip OTP
      onSessionAuth({ email: form.email.trim().toLowerCase(), verifiedToken: token });

      await handleSubscribeWithToken(token);
    } catch (err: any) {
      if (otpStatus === 'verified' || verifiedToken) {
        setStatus('error'); setMessage(err.message || 'Subscription failed.');
      } else {
        setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
      }
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setVerifiedToken(null); setShowTurnstile(true);
  };

  if (status === 'success') {
    return (
      <div>
        <BackArrow onClick={onBack} />
        <div className="ls-success-screen">
          <div className="ls-success-icon">
            {deliveryMethod === 'email' ? '✅' : '📱'}
          </div>
          <h2 className="ls-success-title">
            {deliveryMethod === 'email' ? 'Subscribed!' : 'Request Submitted!'}
          </h2>
          <p className="ls-success-message">{message}</p>
        </div>
      </div>
    );
  }

  if (otpStatus === 'sent' || otpStatus === 'verifying' || (otpStatus === 'error' && verifiedToken === null && otpCode !== '')) return (
    <OtpScreen
      email={form.email}
      otpCode={otpCode}
      otpStatus={otpStatus}
      otpMessage={otpMessage}
      confirmLabel={deliveryMethod === 'email' ? 'Confirm & Subscribe' : 'Confirm & Submit Request'}
      confirmLabelLoading="Verifying…"
      onCodeChange={setOtpCode}
      onConfirm={handleVerifyAndSubscribe}
      onResend={handleResendOtp}
      onBack={() => { setOtpStatus('idle'); setOtpCode(''); setOtpMessage(''); setVerifiedToken(null); setShowTurnstile(false); }}
    />
  );

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Subscribe to Alerts</h2>
      <p className="ls-view-subtitle">Get notified when lightning storms start and end near your locations.</p>

      {sessionAuth && (
        <div className="ls-session-inline-banner">
          Verified as <strong>{sessionAuth.email}</strong>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Name *</label>
          <input className="ls-input" placeholder="Your name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="ls-field">
          <label className="ls-label">Email *</label>
          <input
            className="ls-input"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            readOnly={!!sessionAuth}
          />
        </div>

        <OrganizationSelector
          organizations={organizations}
          value={organization}
          onChange={setOrganization}
        />

        <AlertLocationMapPicker
          locations={locations}
          selected={selectedLocs}
          onChange={setSelectedLocs}
        />

        <DeliveryMethodSelector value={deliveryMethod} onChange={setDeliveryMethod} />

        <NotificationScheduleSelector
          value={notificationSchedule}
          onChange={setNotificationSchedule}
        />

        {needsPhone && (
          <div className="ls-field">
            <label className="ls-label">Phone Number *</label>
            <input className="ls-input" type="tel" placeholder="+1 (555) 000-0000" value={form.phone_number}
              onChange={e => setForm({ ...form, phone_number: e.target.value })} />
          </div>
        )}

        <TermsAndConditionsField agreed={agreedToTerms} onChange={setAgreedToTerms} terms={terms} />

        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}

        {status === 'error' && <StatusBanner status="error" message={message} />}

        <button type="submit" className="ls-submit-btn" disabled={status === 'loading' || showTurnstile}>
          {showTurnstile ? 'Sending code…' : deliveryMethod === 'email' ? 'Subscribe' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
};

// ─── Manage View ──────────────────────────────────────────────────────────────

type ManageStep = 'form' | 'otp' | 'prefs' | 'success';

const ManageView = ({
  onBack, locations, terms, organizations, sessionAuth, onSessionAuth,
}: {
  onBack: () => void;
  locations: Location[];
  terms: TermsAndConditions | null;
  organizations: Organization[];
  sessionAuth: SessionAuth | null;
  onSessionAuth: (auth: SessionAuth) => void;
}) => {
  const [step, setStep] = useState<ManageStep>(sessionAuth ? 'prefs' : 'form');
  const [email, setEmail] = useState(sessionAuth?.email ?? '');
  const [verifiedToken, setVerifiedToken] = useState(sessionAuth?.verifiedToken ?? '');
  const [subscriber, setSubscriber] = useState<SubscriberState | null>(null);
  const [organization, setOrganization] = useState<number | null>(null);
  const [notificationSchedule, setNotificationSchedule] = useState<NotificationSchedule>({});

  // OTP state
  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [showTurnstile, setShowTurnstile] = useState(false);

  const [selectedLocs, setSelectedLocs] = useState<number[]>([]);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('email');
  const [showSmsRequest, setShowSmsRequest] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Auto-load subscriber when session is available and we haven't yet
  useEffect(() => {
    if (sessionAuth && step === 'prefs' && !subscriber) {
      loadSubscriber(sessionAuth.email, sessionAuth.verifiedToken);
    }
  }, []);

  const loadSubscriber = async (emailVal: string, tokenVal: string) => {
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const lookupRes = await fetch(`${API_BASE}/public/sms_lookup/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: emailVal, verified_token: tokenVal }),
      });
      const lookupData = await lookupRes.json();
      if (!lookupRes.ok) throw new Error(lookupData.error || 'Could not load subscription.');

      setSubscriber(lookupData);
      setSelectedLocs(lookupData.location_ids);
      setOrganization(lookupData.organization);
      setPhoneNumber(lookupData.phone_number || '');
      setNotificationSchedule(lookupData.notification_schedule ?? null);

      if (lookupData.email_alerts_enabled && lookupData.sms_alerts_enabled) {
        setDeliveryMethod('email_sms');
      } else if (!lookupData.email_alerts_enabled && lookupData.sms_alerts_enabled) {
        setDeliveryMethod('sms');
      } else {
        setDeliveryMethod('email');
      }

      // Keep session in sync
      onSessionAuth({
        email: emailVal,
        verifiedToken: tokenVal,
      });
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Could not load subscription.');
      // Session token may be stale; fall back to email form
      setStep('form');
    }
  };

  const toggleLoc = (id: number) =>
    setSelectedLocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── STEP 1: validate email → show Turnstile ──────────────────────────────
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setStatus('error'); setMessage('Please enter your email.'); return; }
    setStatus('idle'); setMessage('');
    setShowTurnstile(true);
  };

  // ── STEP 2: Turnstile → send OTP ─────────────────────────────────────────
  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');
    setOtpMessage('');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email, turnstileToken: token, flow: 'update' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${email}.`);
      setStep('otp');
    } catch (err: any) {
      const errorMessage = err.message || 'Could not send verification code.';
      if (errorMessage.includes('No subscription found')) {
        setOtpStatus('idle'); setStatus('error'); setMessage(errorMessage); return;
      }
      setOtpStatus('error'); setOtpMessage(errorMessage);
      setStep('otp');
    }
  };

  // ── STEP 3: verify OTP → lookup subscriber ────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) { setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return; }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);

      // NOTE: We do NOT call onSessionAuth here yet — loadSubscriber will call
      // it once it has fetched the subscriber's current state from the server.
      await loadSubscriber(email, token);

      setOtpStatus('verified');
      setStep('prefs');
    } catch (err: any) {
      setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setVerifiedToken(''); setShowTurnstile(true); setStep('form');
  };

  const handleDeliveryMethodChange = (method: DeliveryMethod) => {
    const smsApproved = subscriber?.sms_status === 'approved';
    setDeliveryMethod(method);
    setStatus('idle');
    setMessage('');
    if ((method === 'email_sms' || method === 'sms') && !smsApproved) {
      setShowSmsRequest(true);
    } else {
      setShowSmsRequest(false);
    }
  };

  // ── STEP 4a: save updated prefs + locations ───────────────────────────────
  const handleSavePrefs = async () => {
    if (selectedLocs.length === 0) { setStatus('error'); setMessage('Please select at least one location.'); return; }

    const smsApproved = subscriber?.sms_status === 'approved';

    if (
      smsApproved &&
      (deliveryMethod === 'sms' || deliveryMethod === 'email_sms') &&
      !phoneNumber.trim()
    ) {
      setStatus('error');
      setMessage('A phone number is required for SMS alerts.');
      return;
    }

    setStatus('loading');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_manage/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          email,
          verified_token: verifiedToken,
          locations: selectedLocs,
          delivery_method: deliveryMethod,
          phone_number: smsApproved ? normalizePhoneNumber(phoneNumber) : null,
          organization,
          notification_schedule: notificationSchedule,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed.');

      // Refresh session
      onSessionAuth({ email, verifiedToken });

      setSuccessMessage('Your subscription has been updated successfully.');
      setStep('success');
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Update failed.');
    }
  };

  // ── STEP 4b: submit inline SMS request ───────────────────────────────────
  const handleRequestSms = async () => {
    if (!phoneNumber.trim()) { setStatus('error'); setMessage('Please enter your phone number.'); return; }
    if (!agreedToTerms) { setStatus('error'); setMessage('You must agree to the Terms and Conditions.'); return; }
    setStatus('loading');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_sub_request/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({
          name: subscriber?.name || '',
          email,
          phone_number: normalizePhoneNumber(phoneNumber),
          locations: selectedLocs,
          terms_version: terms?.version,
          verified_token: verifiedToken,
          delivery_method: deliveryMethod,
          organization,
          notification_schedule: notificationSchedule,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed.');

      // Keep session current
      onSessionAuth({ email, verifiedToken });

      setSuccessMessage(data.message || 'Your SMS request has been submitted. You will receive an email once reviewed.');
      setStep('success');
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Request failed.');
    }
  };

  // ── Render: loading subscriber (session path) ─────────────────────────────
  if (step === 'prefs' && !subscriber && status !== 'error') return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Manage Subscription</h2>
      <p className="ls-view-subtitle">Loading your subscription…</p>
    </div>
  );

  // ── Render: success ───────────────────────────────────────────────────────
  if (step === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">✅</div>
        <h2 className="ls-success-title">Updated!</h2>
        <p className="ls-success-message">{successMessage}</p>
      </div>
    </div>
  );

  // ── Render: OTP ───────────────────────────────────────────────────────────
  if (step === 'otp') return (
    <OtpScreen
      email={email}
      otpCode={otpCode}
      otpStatus={otpStatus}
      otpMessage={otpMessage}
      confirmLabel="Verify & Load Subscription"
      confirmLabelLoading="Verifying…"
      onCodeChange={setOtpCode}
      onConfirm={handleVerifyOtp}
      onResend={handleResendOtp}
      onBack={() => { setStep('form'); setOtpStatus('idle'); setOtpCode(''); setOtpMessage(''); }}
    />
  );

  // ── Render: prefs form ────────────────────────────────────────────────────
  if (step === 'prefs' && subscriber) {
    const smsApproved = subscriber.sms_status === 'approved';
    const smsPending = subscriber.sms_status === 'pending';
    const smsDenied = subscriber.sms_status === 'denied';

    const deliveryOptions: { value: DeliveryMethod; label: string; desc: string; badge?: string }[] = [
      {
        value: 'email',
        label: 'Email only',
        desc: 'Receive alerts via email.',
      },
      {
        value: 'email_sms',
        label: 'Email + SMS',
        desc: smsApproved ? 'Receive alerts via both email and SMS.'
          : smsPending ? 'Your SMS request is pending approval.'
            : 'Receive alerts via both email and SMS.',
        badge: smsApproved ? undefined : smsPending ? 'Pending' : 'Requires approval',
      },
      {
        value: 'sms',
        label: 'SMS only',
        desc: smsApproved ? 'Receive alerts via SMS only.'
          : smsPending ? 'Your SMS request is pending approval.'
            : 'Receive alerts via SMS only.',
        badge: smsApproved ? undefined : smsPending ? 'Pending' : 'Requires approval',
      },
    ];

    return (
      <div>
        <BackArrow onClick={() => onBack()} />
        <h2 className="ls-view-title">Manage Subscription</h2>
        <p className="ls-view-subtitle">Update your locations and delivery preferences below.</p>

        {sessionAuth && (
          <div className="ls-session-inline-banner">
            Verified as <strong>{sessionAuth.email}</strong>
          </div>
        )}

        <AlertLocationMapPicker
          locations={locations}
          selected={selectedLocs}
          onChange={setSelectedLocs}
        />

        {smsApproved && (
          <div className="ls-field">
            <label className="ls-label">Phone Number</label>
            <input
              className="ls-input"
              type="tel"
              placeholder="+1 (555) 000-0000"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </div>
        )}

        <OrganizationSelector
          organizations={organizations}
          value={organization}
          onChange={setOrganization}
        />

        <NotificationScheduleSelector
          value={notificationSchedule}
          onChange={setNotificationSchedule}
        />

        <div className="ls-field-last">
          <label className="ls-label">How would you like to receive alerts?</label>

          <div className="ls-delivery-options">
            {deliveryOptions.map(opt => (
              <label
                key={opt.value}
                className={`ls-delivery-option ${deliveryMethod === opt.value ? 'ls-delivery-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="manageDeliveryMethod"
                  value={opt.value}
                  checked={deliveryMethod === opt.value}
                  onChange={() => handleDeliveryMethodChange(opt.value)}
                  className="ls-checkbox-hidden"
                />
                <div className={`ls-delivery-radio ${deliveryMethod === opt.value ? 'ls-checked' : ''}`} />
                <div className="ls-delivery-text">
                  <span className="ls-delivery-label">
                    {opt.label}
                    {opt.badge && <span className="ls-delivery-badge">{opt.badge}</span>}
                  </span>
                  <span className="ls-delivery-desc">{opt.desc}</span>
                </div>
              </label>
            ))}
          </div>

          {smsPending && (
            <div className="ls-info-box" style={{ marginTop: '8px' }}>
              ⏳ Your SMS request is pending review. You&apos;ll receive an email once a decision has been made.
            </div>
          )}
          {smsDenied && (
            <div className="ls-warning-box" style={{ marginTop: '8px' }}>
              ❌ Your previous SMS request was denied. Select Email + SMS or SMS only to submit a new request.
            </div>
          )}

          {showSmsRequest && !smsApproved && (
            <div className="ls-sms-request-inline" style={{ marginTop: '12px' }}>
              <p className="ls-field-hint" style={{ marginBottom: '8px' }}>
                SMS alerts require manual review. You&apos;ll be notified via email once approved or denied.
              </p>
              <label className="ls-label">Phone Number *</label>
              <input
                className="ls-input"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                style={{ marginBottom: '12px' }}
              />
              <TermsAndConditionsField agreed={agreedToTerms} onChange={setAgreedToTerms} terms={terms} />
              {status === 'error' && <StatusBanner status="error" message={message} />}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  type="button"
                  className="ls-submit-btn"
                  disabled={status === 'loading'}
                  onClick={handleRequestSms}
                >
                  {status === 'loading' ? 'Submitting…' : smsDenied ? 'Re-request SMS Alerts' : 'Submit SMS Request'}
                </button>
                <button
                  type="button"
                  className="ls-submit-btn danger"
                  onClick={() => {
                    setShowSmsRequest(false);
                    setDeliveryMethod('email');
                    setStatus('idle');
                    setMessage('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {status === 'error' && !showSmsRequest && <StatusBanner status="error" message={message} />}

        {!showSmsRequest && (
          <>
            <div className="ls-info-box" style={{ marginBottom: '12px' }}>
              🔒 After saving, you&apos;ll need to verify your email again to make further changes.
            </div>
            <button
              type="button"
              className="ls-submit-btn"
              disabled={status === 'loading'}
              onClick={handleSavePrefs}
            >
              {status === 'loading' ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Render: initial email form ────────────────────────────────────────────
  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Manage Subscription</h2>
      <p className="ls-view-subtitle">Enter your email to look up your subscription and make changes.</p>
      <form onSubmit={handleEmailSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input
            className="ls-input"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value.trim().toLowerCase())}
          />
        </div>
        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}
        {status === 'error' && <StatusBanner status="error" message={message} />}
        <button type="submit" className="ls-submit-btn" disabled={showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Continue'}
        </button>
      </form>
    </div>
  );
};

// ─── Unsubscribe View ─────────────────────────────────────────────────────────

const UnsubscribeView = ({
  onBack,
  sessionAuth,
  onSessionAuth,
  onClearSession,
}: {
  onBack: () => void;
  sessionAuth: SessionAuth | null;
  onSessionAuth: (auth: SessionAuth) => void;
  onClearSession: () => void;
}) => {
  const [form, setForm] = useState({ email: sessionAuth?.email ?? '' });
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const [otpStatus, setOtpStatus] = useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState<string | null>(sessionAuth?.verifiedToken ?? null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) { setStatus('error'); setMessage('Please enter your email.'); return; }
    setStatus('idle'); setMessage('');

    // Session bypass: skip Turnstile + OTP
    if (sessionAuth && sessionAuth.email === form.email.trim().toLowerCase() && sessionAuth.verifiedToken) {
      setVerifiedToken(sessionAuth.verifiedToken);
      handleUnsubscribeWithToken(sessionAuth.verifiedToken);
    } else {
      setShowTurnstile(true);
    }
  };

  const handleUnsubscribeWithToken = async (token: string) => {
    setStatus('loading');
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setStatus('error'); setMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_unsubscribe/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, verified_token: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unsubscribe failed.');
      setStatus('success');
      setMessage('You have been successfully unsubscribed from all lightning alerts.');
      // Clear the session since the subscription is gone
      onClearSession();
    } catch (err: any) {
      setStatus('error'); setMessage(err.message || 'Unsubscribe failed.');
    }
  };

  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, turnstileToken: token, flow: 'unsubscribe' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`A 6-digit verification code was sent to ${form.email}.`);
    } catch (err: any) {
      const errorMessage = err.message || 'Could not send verification code.';
      if (errorMessage.includes('No subscription found')) {
        setOtpStatus('idle'); setStatus('error'); setMessage(errorMessage); return;
      }
      setOtpStatus('error'); setOtpMessage(errorMessage);
    }
  };

  const handleVerifyAndUnsubscribe = async () => {
    if (otpCode.length !== 6) { setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return; }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: form.email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const token = verifyData.verified_token;
      setVerifiedToken(token);
      // Persist to session before unsubscribing
      onSessionAuth({ email: form.email.trim().toLowerCase(), verifiedToken: token });
      setOtpStatus('verified');

      await handleUnsubscribeWithToken(token);
    } catch (err: any) {
      if (verifiedToken || otpStatus === 'verified') {
        setStatus('error'); setMessage(err.message || 'Unsubscribe failed.');
      } else {
        setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
      }
    }
  };

  const handleResendOtp = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setVerifiedToken(null); setShowTurnstile(true);
  };

  if (status === 'success') return (
    <div>
      <BackArrow onClick={onBack} />
      <div className="ls-success-screen">
        <div className="ls-success-icon">👋</div>
        <h2 className="ls-success-title">Unsubscribed</h2>
        <p className="ls-success-message">{message}</p>
      </div>
    </div>
  );

  if (otpStatus === 'sent' || otpStatus === 'verifying' || otpStatus === 'error') return (
    <OtpScreen
      email={form.email}
      otpCode={otpCode}
      otpStatus={otpStatus}
      otpMessage={otpMessage}
      confirmLabel="Confirm & Unsubscribe"
      confirmLabelLoading="Verifying…"
      isDanger
      onCodeChange={setOtpCode}
      onConfirm={handleVerifyAndUnsubscribe}
      onResend={handleResendOtp}
      onBack={() => { setOtpStatus('idle'); setOtpCode(''); setOtpMessage(''); setVerifiedToken(null); setShowTurnstile(false); }}
    />
  );

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Unsubscribe</h2>
      <p className="ls-view-subtitle">Enter your email to remove yourself from all lightning alerts.</p>
      <div className="ls-warning-box">
        ⚠️ This will permanently delete your subscription and all associated locations.
      </div>

      {sessionAuth && (
        <div className="ls-session-inline-banner">
          Verified as <strong>{sessionAuth.email}</strong>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input
            className="ls-input"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            readOnly={!!sessionAuth}
          />
        </div>
        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}
        {status === 'error' && <StatusBanner status="error" message={message} />}
        <button type="submit" className="ls-submit-btn danger" disabled={status === 'loading' || showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Unsubscribe'}
        </button>
      </form>
    </div>
  );
};

// ─── Storm Map View ───────────────────────────────────────────────────────────

import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';

function useCountdown(targetTime: string | null): string {
  const [display, setDisplay] = React.useState('');

  React.useEffect(() => {
    if (!targetTime) { setDisplay(''); return; }

    const tick = () => {
      const last = new Date(targetTime).getTime();
      const remaining = 30 * 60 * 1000 - (Date.now() - last);
      if (remaining <= 0) {
        setDisplay('All clear soon…');
      } else {
        const m = Math.floor(remaining / 60000);
        const s = Math.floor((remaining % 60000) / 1000);
        setDisplay(`${m}m ${s.toString().padStart(2, '0')}s`);
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetTime]);

  return display;
}

const CountdownBadge = ({ lastSeen }: { lastSeen: string | null }) => {
  const display = useCountdown(lastSeen);
  return <span>{display}</span>;
};

function makeStormIcon(color: string, borderColor: string, label: string) {
  return L.divIcon({
    className: '',
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -38],
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
        <filter id="shadow" x="-30%" y="-20%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)" />
        </filter>
        <path
          d="M14 2C8.477 2 4 6.477 4 12c0 7 10 22 10 22s10-15 10-22c0-5.523-4.477-10-10-10z"
          fill="${color}"
          stroke="${borderColor}"
          stroke-width="1.5"
          filter="url(#shadow)"
        />
        <circle cx="14" cy="12" r="4.5" fill="white" opacity="0.9" />
      </svg>
    `,
  });
}

type StormMapStep = 'map' | 'otp_email' | 'otp_verify' | 'filtered';

const MapResizer = ({ trigger }: { trigger: boolean }) => {
  const map = useMap();

  React.useEffect(() => {
    const id = setTimeout(() => {
      if (map && map.getContainer()) {
        map.invalidateSize();
      }
    }, 50);

    return () => clearTimeout(id);
  }, [trigger, map]);

  return null;
};

const StormMapView = ({
  onBack,
  sessionAuth,
  onSessionAuth,
}: {
  onBack: () => void;
  sessionAuth: SessionAuth | null;
  onSessionAuth: (auth: SessionAuth) => void;
}) => {
  const [filter, setFilter] = React.useState<'all' | 'active' | 'clear'>('all');
  const [myLocations, setMyLocations] = React.useState<LocationWithStorm[]>([]);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Use verifiedToken as the signal for whether we can go straight to the map
  const [step, setStep] = React.useState<StormMapStep>(
    sessionAuth?.verifiedToken ? 'map' : 'otp_email'
  );

  const [email, setEmail] = React.useState(sessionAuth?.email ?? '');
  const [showTurnstile, setShowTurnstile] = React.useState(false);
  const [otpStatus, setOtpStatus] = React.useState<OtpStatus>('idle');
  const [otpCode, setOtpCode] = React.useState('');
  const [otpMessage, setOtpMessage] = React.useState('');
  const [authStatus, setAuthStatus] = React.useState<Status>('idle');
  const [authMessage, setAuthMessage] = React.useState('');

  // Fetches this subscriber's own locations (with live storm status) from the
  // gated endpoint — never the public picker list, which no longer carries
  // storm data.
  const fetchMyLocations = React.useCallback(async (emailVal: string, tokenVal: string) => {
    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) return;
    try {
      const res = await fetch(`${API_BASE}/public/sms_my_locations/`, {
        method: 'POST',
        credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email: emailVal, verified_token: tokenVal }),
      });
      if (res.ok) {
        const data: LocationWithStorm[] = await res.json();
        setMyLocations(data);
      }
    } catch {
      // Silent fail — map will just show no pins
    }
  }, []);

  // Initial load if we already have a verified session (e.g. arrived here
  // after subscribing/managing elsewhere in this tab).
  React.useEffect(() => {
    if (sessionAuth?.verifiedToken) {
      fetchMyLocations(sessionAuth.email, sessionAuth.verifiedToken);
    }
  }, []);

  // Poll for live storm status while this view is open.
  React.useEffect(() => {
    if (!sessionAuth?.verifiedToken) return;
    const id = setInterval(() => {
      fetchMyLocations(sessionAuth.email, sessionAuth.verifiedToken);
    }, 30_000);
    return () => clearInterval(id);
  }, [sessionAuth?.verifiedToken, sessionAuth?.email, fetchMyLocations]);

  const visibleLocations = React.useMemo(() => {
    if (filter === 'active') return myLocations.filter(l => l.is_active_storm);
    if (filter === 'clear') return myLocations.filter(l => !l.is_active_storm);
    return myLocations;
  }, [myLocations, filter]);

  const getMarkerStyle = (loc: LocationWithStorm): { color: string; border: string } => {
    if (!loc.is_active_storm) return { color: '#22c55e', border: '#16a34a' };
    if (!loc.last_seen_strike_time) return { color: '#f59e0b', border: '#d97706' };
    const age = Date.now() - new Date(loc.last_seen_strike_time).getTime();
    const pct = Math.min(age / (30 * 60 * 1000), 1);
    if (pct < 0.33) return { color: '#ef4444', border: '#b91c1c' };
    if (pct < 0.66) return { color: '#f97316', border: '#c2410c' };
    return { color: '#eab308', border: '#a16207' };
  };

  // ── OTP flow ──────────────────────────────────────────────────────────────

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setAuthStatus('error'); setAuthMessage('Please enter your email.'); return; }
    setAuthStatus('idle'); setAuthMessage('');
    setShowTurnstile(true);
  };

  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstile(false);
    setOtpStatus('sending');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const res = await fetch(`${API_BASE}/public/sms_request_otp/`, {
        method: 'POST', credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email, turnstileToken: token, flow: 'update' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      setOtpStatus('sent');
      setOtpMessage(`Code sent to ${email}.`);
      setStep('otp_verify');
    } catch (err: any) {
      const msg = err.message || 'Could not send code.';
      if (msg.includes('No subscription found')) {
        setOtpStatus('idle'); setAuthStatus('error'); setAuthMessage(msg); return;
      }
      setOtpStatus('error'); setOtpMessage(msg);
      setStep('otp_verify');
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) { setOtpStatus('error'); setOtpMessage('Please enter the 6-digit code.'); return; }
    setOtpStatus('verifying');

    const csrfToken = await fetchCsrfToken();
    if (!csrfToken) { setOtpStatus('error'); setOtpMessage('Could not get CSRF token.'); return; }

    try {
      const verifyRes = await fetch(`${API_BASE}/public/sms_verify_otp/`, {
        method: 'POST', credentials: "same-origin",
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
        body: JSON.stringify({ email, code: otpCode }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed.');

      const verifiedToken = verifyData.verified_token;

      // Fetch this subscriber's own storm-inclusive locations from the
      // gated endpoint — replaces the old direct sms_lookup call here.
      await fetchMyLocations(email, verifiedToken);

      // Persist to session so other views can skip OTP
      onSessionAuth({
        email: email.trim().toLowerCase(),
        verifiedToken,
      });

      setOtpStatus('verified');
      setStep('map');
    } catch (err: any) {
      setOtpStatus('error'); setOtpMessage(err.message || 'Verification failed.');
    }
  };

  const handleResend = () => {
    setOtpStatus('idle'); setOtpCode(''); setOtpMessage('');
    setShowTurnstile(true); setStep('otp_email');
  };

  // ── Render: email entry ───────────────────────────────────────────────────
  if (step === 'otp_email') return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Storm Map</h2>
      <p className="ls-view-subtitle">Enter your email to view storm activity at your subscribed locations.</p>
      <form onSubmit={handleEmailSubmit}>
        <div className="ls-field">
          <label className="ls-label">Email</label>
          <input className="ls-input" type="email" placeholder="you@example.com"
            value={email} onChange={e => setEmail(e.target.value.trim().toLowerCase())} />
        </div>
        {showTurnstile && (
          <div className="ls-turnstile">
            <Turnstile siteKey={TURNSTILE_SITE_KEY}
              options={{ theme: 'light', size: 'normal', language: 'auto' }}
              onSuccess={handleTurnstileSuccess} />
          </div>
        )}
        {authStatus === 'error' && <StatusBanner status="error" message={authMessage} />}
        <button type="submit" className="ls-submit-btn" disabled={showTurnstile}>
          {showTurnstile ? 'Sending code…' : 'Continue'}
        </button>
      </form>
    </div>
  );

  // ── Render: OTP verify ────────────────────────────────────────────────────
  if (step === 'otp_verify') return (
    <OtpScreen
      email={email}
      otpCode={otpCode}
      otpStatus={otpStatus}
      otpMessage={otpMessage}
      confirmLabel="Show my locations"
      confirmLabelLoading="Verifying…"
      onCodeChange={setOtpCode}
      onConfirm={handleVerifyOtp}
      onResend={handleResend}
      onBack={() => { setStep('otp_email'); setOtpStatus('idle'); setOtpCode(''); setOtpMessage(''); }}
    />
  );

  // ── Render: authenticated map ─────────────────────────────────────────────
  const activeCount = visibleLocations.filter(l => l.is_active_storm).length;

  return (
    <div>
      <BackArrow onClick={onBack} />
      <h2 className="ls-view-title">Storm Map</h2>
      <p className="ls-view-subtitle">
        {activeCount > 0
          ? `${activeCount} of your location${activeCount > 1 ? 's' : ''} has active storm activity.`
          : 'No active storm activity at any of your locations.'}
      </p>

      {sessionAuth && (
        <div className="ls-session-inline-banner">
          Viewing locations for <strong>{sessionAuth.email}</strong>
        </div>
      )}

      <div className="sm-filters">
        <div className="sm-filter-group">
          {(['all', 'active', 'clear'] as const).map(f => (
            <button
              key={f}
              className={`sm-filter-btn${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Active storms' : 'Clear'}
            </button>
          ))}
        </div>
      </div>

      <div className="sm-legend">
        <span className="sm-legend-dot" style={{ background: '#ef4444' }} /> Recent strike
        <span className="sm-legend-dot" style={{ background: '#f97316', marginLeft: 10 }} /> Fading
        <span className="sm-legend-dot" style={{ background: '#eab308', marginLeft: 10 }} /> Near clear
        <span className="sm-legend-dot" style={{ background: '#22c55e', marginLeft: 10 }} /> No storm
      </div>

      <div className={`sm-map-wrapper${isFullscreen ? ' sm-map-fullscreen' : ''}`}>
        <button
          className="sm-fullscreen-btn"
          onClick={() => setIsFullscreen(f => !f)}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          type="button"
        >
          {isFullscreen ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M5 1H1v4M11 1h4v4M5 15H1v-4M11 15h4v-4M6 6l-5-5M10 6l5-5M6 10l-5 5M10 10l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1 5V1h4M11 1h4v4M1 11v4h4M15 11v4h-4M1 1l5 5M15 1l-5 5M1 15l5-5M15 15l-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </button>

        <MapContainer
          center={[28.5, -82.5]}
          zoom={7}
          minZoom={3}
          zoomControl={false}
          zoomAnimation={false}
          style={{ width: '100%', height: '100%' }}
        >
          <MapResizer trigger={isFullscreen} />
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {visibleLocations.map(loc => {
            const { color, border } = getMarkerStyle(loc);
            const icon = makeStormIcon(color, border, loc.location_name);
            const colorKey = loc.is_active_storm
              ? (loc.last_seen_strike_time
                ? Math.floor(Math.min(
                  (Date.now() - new Date(loc.last_seen_strike_time).getTime()) / (30 * 60 * 1000),
                  1
                ) * 3)
                : 'active-no-time')
              : 'clear';

            return (
              <Marker
                key={`${loc.location_id}-${colorKey}`}
                position={[parseFloat(loc.lat), parseFloat(loc.lon)]}
                icon={icon}
              >
                <Tooltip permanent direction="top" offset={[0, -38]} className="sm-tooltip">
                  {loc.location_name}
                </Tooltip>
                <Popup className="sm-popup" minWidth={200}>
                  <div className="sm-popup-inner">
                    <div className="sm-popup-name">{loc.location_name}</div>
                    {loc.is_active_storm ? (
                      <>
                        <div className="sm-popup-badge active">Active storm</div>
                        <div className="sm-popup-row">
                          <span>All clear in</span>
                          <strong><CountdownBadge lastSeen={loc.last_seen_strike_time} /></strong>
                        </div>
                        {loc.last_seen_strike_time && (
                          <div className="sm-popup-row">
                            <span>Last strike</span>
                            <strong>
                              {new Date(loc.last_seen_strike_time).toLocaleTimeString([], {
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </strong>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="sm-popup-badge clear">No active storm</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {visibleLocations.length === 0 && (
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: 13, marginTop: 12 }}>
          No locations match the current filter.
        </p>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const LightningSubscription = () => {
  const [view, setView] = useState<View>('menu');
  const [locations, setLocations] = useState<Location[]>([]);
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const [sessionAuth, setSessionAuthState] = useState<SessionAuth | null>(getSessionAuth());

  const handleSessionAuth = (auth: SessionAuth) => {
    setSessionAuth(auth);
    setSessionAuthState(auth);
  };

  const handleClearSession = () => {
    setSessionAuth(null);
    setSessionAuthState(null);
  };

  useEffect(() => {
    // Public picker list — no storm data, safe to show to anyone.
    fetch(`${API_BASE}/public/sms_locations`)
      .then(r => r.ok ? r.json() : [])
      .then(setLocations)
      .catch(() => setLocations([]));

    fetch(`${API_BASE}/public/sms_terms`)
      .then(r => r.ok ? r.json() : null)
      .then(setTerms)
      .catch(() => setTerms(null));

    fetch(`${API_BASE}/public/sms_organizations`)
      .then(r => r.ok ? r.json() : [])
      .then(setOrganizations)
      .catch(() => setOrganizations([]));
  }, []);

  return (
    <div className="ls-page">
      <div className="ls-header">
        <img
          src={fnnLogo}
          alt="FNN Logo"
          className="fnn-header-logo ls-logo"
          onClick={() => window.open('https://fireneuralnetwork.com/', '_blank', 'noopener,noreferrer')}
        />
        <h1 className="ls-header-title">FNN™ Alert System</h1>
      </div>

      <div className="ls-center">
        <div className="ls-panel">
          {view === 'menu' && (
            <MenuView
              onSelect={setView}
              sessionAuth={sessionAuth}
              onClearSession={handleClearSession}
            />
          )}
          {view === 'subscribe' && (
            <SubscribeView
              onBack={() => setView('menu')}
              locations={locations}
              terms={terms}
              organizations={organizations}
              sessionAuth={sessionAuth}
              onSessionAuth={handleSessionAuth}
            />
          )}
          {view === 'manage' && (
            <ManageView
              onBack={() => setView('menu')}
              locations={locations}
              terms={terms}
              organizations={organizations}
              sessionAuth={sessionAuth}
              onSessionAuth={handleSessionAuth}
            />
          )}
          {view === 'unsubscribe' && (
            <UnsubscribeView
              onBack={() => setView('menu')}
              sessionAuth={sessionAuth}
              onSessionAuth={handleSessionAuth}
              onClearSession={handleClearSession}
            />
          )}
          {view === 'storm_map' && (
            <StormMapView
              onBack={() => setView('menu')}
              sessionAuth={sessionAuth}
              onSessionAuth={handleSessionAuth}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LightningSubscription;